import { FunctionComponent, ReactNode, useEffect, useLayoutEffect, useState } from 'react';

import { Spinner } from './spinner';

/** Keep the post-login splash up long enough for the logo animation to finish. */
const AUTHENTICATED_SPLASH_MS = 2000;
const SPLASH_EXIT_MS = 500;
const BOOT_SPLASH_INTRO_KEY = 'kh:boot-splash-intro-at';
const BOOT_SPLASH_INTRO_MAX_AGE_MS = 12000;
const SKIP_BOOT_SPLASH_KEY = 'kh:skip-boot-splash';

/** Next full page load (logout returns through Keycloak) should not replay the splash. */
export const markSkipBootSplash = (): void => {
  try {
    sessionStorage.setItem(SKIP_BOOT_SPLASH_KEY, '1');
  } catch {
    // sessionStorage can be unavailable in private mode
  }
};

const consumeSkipBootSplash = (): boolean => {
  try {
    if (sessionStorage.getItem(SKIP_BOOT_SPLASH_KEY) !== '1') return false;
    sessionStorage.removeItem(SKIP_BOOT_SPLASH_KEY);
    return true;
  } catch {
    return false;
  }
};

/** The public welcome screen (`/` and `/welcome`) should never cover itself with the splash. */
export const isWelcomePage = (pathname: string): boolean => {
  const path = pathname.replace(/\/+$/, '') || '/';
  return path === '/' || path === '/welcome';
};

const HIDE_BOOT_SPLASH_EVENT = 'kh:hide-boot-splash';

export const hideBootSplashOnWelcome = (pathname: string): void => {
  if (!isWelcomePage(pathname)) return;
  window.dispatchEvent(new Event(HIDE_BOOT_SPLASH_EVENT));
};

const readBootSplashIntro = (): number | null => {
  try {
    const existing = Number(sessionStorage.getItem(BOOT_SPLASH_INTRO_KEY));
    if (!Number.isFinite(existing)) return null;
    const age = Date.now() - existing;
    if (age >= 0 && age < BOOT_SPLASH_INTRO_MAX_AGE_MS) return existing;
  } catch {
    return null;
  }
  return null;
};

/** Reuse an in-progress intro across a Keycloak redirect or a splash remount. */
const rememberBootSplashIntro = (): number => {
  const existing = readBootSplashIntro();
  if (existing !== null) return existing;
  const now = Date.now();
  try {
    sessionStorage.setItem(BOOT_SPLASH_INTRO_KEY, String(now));
  } catch {
    // sessionStorage can be unavailable in private mode
  }
  return now;
};

const clearBootSplashIntro = (): void => {
  try {
    sessionStorage.removeItem(BOOT_SPLASH_INTRO_KEY);
  } catch {
    // sessionStorage can be unavailable in private mode
  }
};

type BootSplashProps = {
  bootDataReady: boolean;
  authenticated: boolean;
  children: ReactNode;
};

export const BootSplash: FunctionComponent<BootSplashProps> = ({ bootDataReady, authenticated, children }) => {
  const [skipSplash] = useState(() => consumeSkipBootSplash() || isWelcomePage(window.location.pathname));
  const [introStartedAt, setIntroStartedAt] = useState<number | null>(null);
  const [phase, setPhase] = useState<'visible' | 'exiting' | 'gone'>(skipSplash ? 'gone' : 'visible');
  const [appReleased, setAppReleased] = useState(skipSplash || bootDataReady);

  useEffect(() => {
    if (bootDataReady) setAppReleased(true);
  }, [bootDataReady]);

  useLayoutEffect(() => {
    const hide = () => {
      setPhase('gone');
      setAppReleased(true);
    };
    window.addEventListener(HIDE_BOOT_SPLASH_EVENT, hide);
    return () => window.removeEventListener(HIDE_BOOT_SPLASH_EVENT, hide);
  }, []);

  useEffect(() => {
    if (skipSplash || !bootDataReady || introStartedAt !== null) return;
    setIntroStartedAt(rememberBootSplashIntro());
  }, [bootDataReady, introStartedAt, skipSplash]);

  useEffect(() => {
    if (!bootDataReady || phase !== 'visible' || introStartedAt === null) return;

    const elapsed = Date.now() - introStartedAt;
    const minimum = authenticated ? AUTHENTICATED_SPLASH_MS : 0;
    const wait = Math.max(0, minimum - elapsed);
    const exitTimer = window.setTimeout(() => setPhase('exiting'), wait);

    return () => window.clearTimeout(exitTimer);
  }, [authenticated, bootDataReady, introStartedAt, phase]);

  useEffect(() => {
    if (phase !== 'exiting') return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const exitMs = reducedMotion ? 0 : authenticated ? SPLASH_EXIT_MS : 200;
    const doneTimer = window.setTimeout(() => setPhase('gone'), exitMs);

    return () => window.clearTimeout(doneTimer);
  }, [authenticated, phase]);

  useEffect(() => {
    if (phase !== 'gone') return;
    clearBootSplashIntro();
  }, [phase]);

  return (
    <>
      {appReleased ? children : null}
      {phase !== 'gone' ? <Spinner exiting={phase === 'exiting'} introStartedAt={introStartedAt} /> : null}
    </>
  );
};
